import type { CurrentUser } from '@ecc/shared';

/**
 * Client API.
 *
 * La sessione viaggia in un cookie `HttpOnly`, quindi `credentials: 'include'`
 * e nessun token da gestire lato JavaScript: in `localStorage` non finisce mai
 * nulla di autenticazione (§10.1).
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId?: string,
  ) {
    super(message);
  }
}

/** Errore di rete: distinto da una risposta di errore del server. */
export class NetworkError extends Error {}

type ErrorBody = { code?: string; message?: string; requestId?: string };

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;

  try {
    response = await fetch(path, {
      ...init,
      credentials: 'include',
      headers: {
        // Con FormData il content-type lo imposta il browser, perché deve
        // includere il boundary del multipart: sovrascriverlo qui renderebbe
        // il corpo illeggibile al server.
        ...(init.body && !(init.body instanceof FormData)
          ? { 'content-type': 'application/json' }
          : {}),
        ...init.headers,
      },
    });
  } catch (cause) {
    // Distinguere l'assenza di rete da un rifiuto del server è essenziale per
    // il comportamento offline: il primo caso è recuperabile (§9).
    throw new NetworkError('Richiesta non riuscita', { cause });
  }

  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    // Il server risponde sempre JSON sotto /api. HTML qui significa che
    // qualcosa intercetta le route API — vedi il fallback SPA lato server.
    throw new ApiError(response.status, 'UNEXPECTED_RESPONSE', 'Risposta non attesa dal server.');
  }

  const body: unknown = await response.json();

  if (!response.ok) {
    const error = body as ErrorBody;
    throw new ApiError(
      response.status,
      error.code ?? 'UNKNOWN',
      error.message ?? 'Errore.',
      error.requestId,
    );
  }

  return body as T;
}

export type EventSummary = {
  id: string;
  name: string;
  slug: string;
  location: string | null;
  startsAt: string;
  endsAt: string;
  status: 'DRAFT' | 'ACTIVE' | 'CLOSED' | 'ARCHIVED';
  activePrivacyPolicyId: string | null;
  languages: readonly string[];
};

export type RequestableItem = {
  id: string;
  type: 'DOCUMENT' | 'CATALOG' | 'PRODUCT_INFO';
  /** Etichetta per lingua. Una voce senza etichetta nella lingua servita non viene offerta. */
  labels: Record<string, string>;
};

export type EventDetail = EventSummary & {
  branding: {
    imageAssetId: string | null;
    imageAltText: string | null;
    headline: string | null;
    shortDescription: string | null;
  };
  requestableItems: RequestableItem[];
  /** Etichetta per lingua della casella di ricontatto; `null` usa la predefinita. */
  callbackLabels: Record<string, string> | null;
};

export type PolicySummary = {
  id: string;
  version: string;
  language: string;
  title: string;
  published: boolean;
  publishedAt: string | null;
  /** Valorizzata quando la versione è stata ritirata: resta prova, non è più selezionabile. */
  retiredAt: string | null;
};

export type DeviceSummary = {
  id: string;
  label: string;
  eventId: string;
  status: 'PENDING' | 'ACTIVE' | 'REVOKED';
  pairedAt: string | null;
  lastSeenAt: string | null;
  revokedAt: string | null;
};

export const api = {
  login: (email: string, password: string) =>
    request<{ id: string; role: string; expiresAt: string }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  logout: () => request<void>('/api/auth/logout', { method: 'POST' }),

  me: () => request<CurrentUser>('/api/auth/me'),

  listEvents: () => request<{ events: EventSummary[] }>('/api/events'),

  getEvent: (id: string) => request<EventDetail>(`/api/events/${id}`),

  updateEvent: (id: string, patch: Record<string, unknown>) =>
    request<EventDetail>(`/api/events/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  createEvent: (input: Record<string, unknown>) =>
    request<EventDetail>('/api/events', { method: 'POST', body: JSON.stringify(input) }),

  listPolicies: () => request<{ policies: PolicySummary[] }>('/api/privacy-policies'),

  getPolicy: (id: string) => request<PolicyDetail>(`/api/privacy-policies/${id}`),

  createPolicy: (input: {
    version: string;
    language: string;
    title: string;
    content: string;
  }) =>
    request<PolicyDetail>('/api/privacy-policies', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /** Applicabile solo a una bozza: una versione pubblicata è immutabile. */
  updatePolicyDraft: (id: string, input: { title?: string; content?: string }) =>
    request<PolicyDetail>(`/api/privacy-policies/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),

  deletePolicyDraft: (id: string) =>
    request<void>(`/api/privacy-policies/${id}`, { method: 'DELETE' }),

  retirePolicy: (id: string) =>
    request<PolicyDetail>(`/api/privacy-policies/${id}/retire`, { method: 'POST' }),

  publishPolicy: (id: string) =>
    request<PolicyDetail>(`/api/privacy-policies/${id}/publish`, { method: 'POST' }),

  /**
   * Scarica un export.
   *
   * Non usa `request()` perché la risposta è un file, non JSON. Il browser
   * riceve un allegato con header no-store: nulla resta in cache.
   */
  downloadExport: async (
    eventId: string,
    purpose: string,
    reason: string,
  ): Promise<{ blob: Blob; filename: string; rowCount: number }> => {
    const response = await fetch('/api/exports', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ eventId, purpose, reason }),
    });

    if (!response.ok) {
      const body: unknown = await response.json().catch(() => ({}));
      const error = body as { code?: string; message?: string };
      throw new ApiError(response.status, error.code ?? 'EXPORT_FAILED', error.message ?? 'Errore.');
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const match = /filename="([^"]+)"/.exec(disposition);

    return {
      blob: await response.blob(),
      filename: match?.[1] ?? 'export.csv',
      rowCount: Number(response.headers.get('x-export-row-count') ?? '0'),
    };
  },

  listAudit: (action?: string) =>
    request<{ events: AuditEventRecord[] }>(
      `/api/audit${action ? `?action=${encodeURIComponent(action)}` : ''}`,
    ),

  listDevices: (eventId: string) =>
    request<{ devices: DeviceSummary[] }>(`/api/devices?eventId=${encodeURIComponent(eventId)}`),

  /**
   * Registra un tablet. Il `pairingToken` restituito è leggibile solo qui:
   * il server ne conserva il solo hash.
   */
  registerDevice: (eventId: string, label: string) =>
    request<{ device: DeviceSummary; pairingToken: string }>('/api/devices/pair', {
      method: 'POST',
      body: JSON.stringify({ eventId, label }),
    }),

  /**
   * Rigenera il codice di un tablet esistente. Il precedente cessa subito di
   * funzionare: il token non è recuperabile, si sostituisce.
   */
  reissueDeviceToken: (id: string) =>
    request<{ device: DeviceSummary; pairingToken: string }>(`/api/devices/${id}/reissue`, {
      method: 'POST',
    }),

  revokeDevice: (id: string) =>
    request<void>(`/api/devices/${id}/revoke`, { method: 'POST' }),

  listContacts: (eventId: string) =>
    request<{ contacts: ContactRecord[] }>(
      `/api/contacts?eventId=${encodeURIComponent(eventId)}`,
    ),

  withdrawConsent: (contactId: string, consentType: string, reason: string) =>
    request<unknown>(`/api/contacts/${contactId}/consents/withdraw`, {
      method: 'POST',
      body: JSON.stringify({ consentType, reason }),
    }),

  eraseContact: (contactId: string, reason: string) =>
    request<void>(`/api/contacts/${contactId}`, {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    }),

  /** Anteprima in sola lettura: non apre alcuna sessione kiosk. */
  kioskPreview: (eventId: string, language?: string) =>
    request<KioskPreview>(
      `/api/events/${eventId}/kiosk-preview${language ? `?language=${encodeURIComponent(language)}` : ''}`,
    ),
};

export type PolicyDetail = PolicySummary & {
  content: string;
  hash: string;
};

export type AuditEventRecord = {
  id: string;
  action: string;
  actorId: string | null;
  targetType: string | null;
  targetId: string | null;
  occurredAt: string;
  context: Record<string, string | number | boolean> | null;
};

export type ConsentStatusRecord = {
  consentType: string;
  granted: boolean;
  since: string | null;
  policyVersion: string | null;
  consentTextVersion: string | null;
};

export type ConsentEventRecord = {
  id: string;
  consentType: string;
  action: 'GRANTED' | 'WITHDRAWN';
  recordedAt: string;
  policyVersion: string;
  policyLanguage: string;
  consentTextVersion: string | null;
  source: string;
  reason: string | null;
};

export type ContactRecord = {
  id: string;
  firstName: string;
  lastName: string;
  company: string | null;
  email: string;
  eventId: string;
  createdAt: string;
  anonymized: boolean;
  requests: {
    id: string;
    type: string;
    /** Quale materiale è stato chiesto. `null` per il ricontatto commerciale. */
    resourceId: string | null;
    /** Etichette correnti della voce, per lingua. `null` se non più configurata. */
    itemLabels: Record<string, string> | null;
    requestedAt: string;
    policyVersion: string;
  }[];
  consentStatuses: ConsentStatusRecord[];
  consentHistory: ConsentEventRecord[];
};

/**
 * Carica l'immagine di una fiera.
 *
 * `altText` è obbligatorio: il server rifiuta un caricamento senza, e il
 * database rifiuta comunque la coppia incompleta (§20.6).
 */
export function uploadEventBanner(eventId: string, file: File, altText: string) {
  const body = new FormData();
  body.append('altText', altText);
  body.append('file', file);
  return request<EventDetail>(`/api/events/${eventId}/branding/image`, {
    method: 'POST',
    body,
  });
}

export function clearEventBanner(eventId: string) {
  return request<EventDetail>(`/api/events/${eventId}/branding/image`, { method: 'DELETE' });
}

export type UserSummary = {
  id: string;
  email: string;
  role: 'ADMIN' | 'OPERATOR';
  active: boolean;
  createdAt: string;
};

/** La password iniziale arriva una volta sola: non è più recuperabile dopo. */
export type CreatedUser = UserSummary & { initialPassword: string };

export const users = {
  list: () => request<{ users: UserSummary[] }>('/api/users'),
  create: (email: string, role: 'ADMIN' | 'OPERATOR') =>
    request<CreatedUser>('/api/users', {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    }),
  setActive: (id: string, active: boolean) =>
    request<UserSummary>(`/api/users/${id}/active`, {
      method: 'PATCH',
      body: JSON.stringify({ active }),
    }),
  changeRole: (id: string, role: 'ADMIN' | 'OPERATOR') =>
    request<UserSummary>(`/api/users/${id}/role`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    }),
  resetPassword: (id: string) =>
    request<{ initialPassword: string }>(`/api/users/${id}/password`, { method: 'POST' }),
};

/**
 * Cambio della propria password.
 *
 * Al successo tutte le sessioni cadono, inclusa quella corrente: il chiamante
 * deve riportare l'utente al login.
 */
export function changeOwnPassword(currentPassword: string, newPassword: string) {
  return request<void>('/api/auth/password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

export type KioskPreview = {
  preview: true;
  event: {
    id: string;
    name: string;
    slug: string;
    status: 'DRAFT' | 'ACTIVE' | 'CLOSED' | 'ARCHIVED';
    languages: readonly string[];
    branding: {
      imageAssetId: string | null;
      imageAltText: string | null;
      headline: string | null;
      shortDescription: string | null;
    };
  };
  language: string;
  callbackLabel: string | null;
  requestableItems: readonly {
    id: string;
    type: 'DOCUMENT' | 'CATALOG' | 'PRODUCT_INFO';
    label: string;
  }[];
  policy: {
    policyId: string;
    version: string;
    language: string;
    title: string;
    content: string;
    hash: string;
  };
  marketingConsentText: {
    consentTextId: string;
    version: string;
    language: string;
    text: string;
    hash: string;
  } | null;
};
